import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import OfficeMap from './components/OfficeMap';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { useLanguage } from '../../contexts/LanguageContext';

const Contact = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      pageTitle: 'Contact Us - Africoffee',
      heading: 'Get in Touch',
      subheading: 'Have questions about our coffee beans or services? We\'re here to help.',
      preferredContact: 'Preferred Contact Methods',
      buyerInquiry: 'For Buyer Inquiries',
      buyerDesc: 'Contact our sales team for pricing, samples, and order information',
      supplierInquiry: 'For Supplier Applications',
      supplierDesc: 'Reach out to our partnership team to discuss supplier opportunities',
      generalInquiry: 'For General Questions',
      generalDesc: 'Use the contact form or email us for any other inquiries'
    },
    zh: {
      pageTitle: '联系我们 - Africoffee',
      heading: '联系我们',
      subheading: '对我们的咖啡豆或服务有疑问？我们随时为您提供帮助。',
      preferredContact: '首选联系方式',
      buyerInquiry: '买家咨询',
      buyerDesc: '联系我们的销售团队了解价格、样品和订单信息',
      supplierInquiry: '供应商申请',
      supplierDesc: '联系我们的合作团队讨论供应商机会',
      generalInquiry: '一般问题',
      generalDesc: '使用联系表格或通过电子邮件向我们咨询任何其他问题'
    }
  };

  const t = content?.[language];

  const contactMethods = [
    {
      icon: 'ShoppingCart',
      title: t?.buyerInquiry,
      description: t?.buyerDesc,
      color: 'var(--color-primary)'
    },
    {
      icon: 'Users',
      title: t?.supplierInquiry,
      description: t?.supplierDesc,
      color: 'var(--color-secondary)'
    },
    {
      icon: 'MessageSquare',
      title: t?.generalInquiry,
      description: t?.generalDesc,
      color: 'var(--color-accent)'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t?.pageTitle}</title>
        <meta name="description" content="Contact Africoffee for inquiries about African green coffee beans, supplier partnerships, and import services. We connect Chinese buyers with verified African coffee suppliers." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px]">
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border-b border-border">
            <FullWidthSection padding="" py="py-12 lg:py-16">
              <ContentWrapper padding="px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto">
                  <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                    {t?.heading}
                  </h1>
                  <p className="text-lg text-muted-foreground">
                    {t?.subheading}
                  </p>
                </div>
              </ContentWrapper>
            </FullWidthSection>
          </div>

          <FullWidthSection padding="" py="py-12 lg:py-16">
            <ContentWrapper padding="px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-6 text-center">
                {t?.preferredContact}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {contactMethods?.map((method, index) => (
                  <div
                    key={index}
                    className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-smooth"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={method?.icon} size={24} color={method?.color} />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      {method?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {method?.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-2">
                <ContactForm language={language} />
              </div>
              <div className="lg:col-span-1">
                <ContactInfo language={language} />
              </div>
            </div>

            <div className="mb-12">
              <OfficeMap language={language} />
            </div>

            <div>
              <FAQSection language={language} />
            </div>
            </ContentWrapper>
          </FullWidthSection>

          <footer className="bg-card border-t border-border mt-16">
            <FullWidthSection padding="" py="py-8">
              <ContentWrapper padding="px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Coffee" size={20} color="var(--color-primary)" />
                    </div>
                    <span className="font-heading font-semibold text-foreground">Africoffee</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    &copy; {new Date()?.getFullYear()} WGIH Africoffee. {language === 'en' ? 'All rights reserved.' : '版权所有。'}
                  </p>
                </div>
              </ContentWrapper>
            </FullWidthSection>
          </footer>
        </main>
      </div>
    </>
  );
};

export default Contact;